import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {useEnv} from "@/hooks/useEnv";
import {useFirebaseAnalytics} from "@/hooks/useFirebaseAnalytics";
import { useDownloadUrls } from "@/hooks/useDownloadUrls";

export default function Footer() {
  const { t } = useTranslation();

  const { appUrl, contactEmail } = useEnv();
  const { appStoreDownloadUrl, apkDownloadUrl } = useDownloadUrls();

  const {
    logEventToAccount,
    logEventDownloadAppStore,
    logEventDownloadApk
  } = useFirebaseAnalytics();

  return (
    <footer className="bg-[#f8fafd] border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col items-center text-center gap-8">

          {/* Brand */}
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0">
                <img
                  src="/logo.svg"
                  alt="BroMoney"
                  className="w-full h-full object-cover object-left"
                />
              </div>
              <span className="text-gray-900 font-bold text-2xl tracking-tight">
                Bro<span className="text-[#007AFF]">Money</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm max-w-sm leading-relaxed">
              {t("footer_desc")}
            </p>
          </div>

          {/* Contact email */}
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center text-[#007AFF]">
              <i className="ri-mail-line text-base" />
            </div>
            <a
              href={`mailto:${contactEmail}`}
              className="text-gray-500 hover:text-[#007AFF] text-sm font-medium transition-colors duration-200 cursor-pointer"
            >
              {contactEmail}
            </a>
          </div>

          {/* 3 action buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={appUrl}
              onClick={() => logEventToAccount('button_bottom')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#007AFF] hover:bg-[#0062CC] text-white rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-global-line text-base" />
              </div>
              {t("footer_goToWallet")}
            </a>

            <a
              href={appStoreDownloadUrl}
              onClick={() => logEventDownloadAppStore('button_bottom')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#007AFF]/40 hover:text-[#007AFF] text-gray-700 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-apple-fill text-base" />
              </div>
              {t("footer_downloadAppStore")}
            </a>

            <a
              href={apkDownloadUrl}
              onClick={() => logEventDownloadApk('button_bottom')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#007AFF]/40 hover:text-[#007AFF] text-gray-700 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-android-line text-base" />
              </div>
              {t("footer_downloadApk")}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
          <p className="text-gray-300 text-xs">
            &copy; {new Date().getFullYear()} {t("footer_copyright")}
          </p>
          <span className="text-gray-200 text-xs hidden sm:inline">·</span>
          <Link
            to="/privacy"
            className="text-gray-300 hover:text-[#007AFF] text-xs transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("footer_privacy")}
          </Link>
          <span className="text-gray-200 text-xs hidden sm:inline">·</span>
          <Link
            to="/terms"
            className="text-gray-300 hover:text-[#007AFF] text-xs transition-colors cursor-pointer whitespace-nowrap"
          >
            {t("footer_terms")}
          </Link>
        </div>
      </div>
    </footer>
  );
}
